// Content configuration for each industry-specific landing page.
// Adding a new industry = add an entry here + register the /for/:slug route.
// Each page uses the same shell (IndustryLandingPage) — only content varies.

import {
  Truck, MapTrifold, Handshake, User, ChartLineUp,
} from "@phosphor-icons/react";

export const INDUSTRY_PAGES = {
  trucking: {
    slug: "trucking",
    icon: Truck,
    eyebrow: "AI FOR TRUCKING COMPANIES",
    title_lead: "Book more loads.",
    title_accent: "Ship more freight.",
    title_close: "Without adding office staff.",
    tagline: "Automate dispatch, invoicing, and customer updates — while you focus on growing your fleet.",
    seo_title: "AI for Trucking Companies · Automate Dispatch, Invoices & Customer Updates",
    seo_description: "FreightPilot AI helps small and mid-size trucking companies save 20-40 hours per week by automating broker email parsing, dispatch, driver communication, and invoicing.",
    pain_points: [
      "Broker rate-cons pile up in your inbox — you're typing loads into a spreadsheet at 11pm",
      "Missed pickup times because a load slipped through the cracks",
      "Customers calling for status updates when the truck is on the highway",
      "Invoices taking 3+ days to send after delivery — cash flow suffers",
      "You need to hire another dispatcher, but 5 more trucks won't pay for it",
    ],
    solutions: [
      { icon: "email", title: "AI reads every broker email", detail: "Paste any rate confirmation → origin, destination, rate, weight, notes extracted in 6 seconds. No typing." },
      { icon: "dispatch", title: "Auto-dispatch to the right driver", detail: "Loads assigned by lane, HOS status, and truck availability. Driver gets SMS pickup notification instantly." },
      { icon: "customer", title: "Broker-facing status emails", detail: "Picked up, in transit, delivered — customers get an update the second the driver hits a milestone." },
      { icon: "invoice", title: "Same-day invoicing", detail: "PDF invoice + rate con + BOL emailed automatically the moment 'delivered' is confirmed. Cash flow accelerates." },
      { icon: "insights", title: "Real-time fleet visibility", detail: "One dashboard: revenue this week, drivers available, loads at risk of missing appointment." },
    ],
    testimonial_placeholder: "You could be our first case study — pilot slots are open through Q1 2026.",
    proof_stat: "20-40",
    proof_unit: "hours saved per week",
    proof_context: "Time back per dispatcher/owner-operator for growth work instead of paperwork.",
    cta_primary: "Try live demo — no signup",
    cta_secondary: "Book a 15-min walkthrough",
  },

  dispatch: {
    slug: "dispatch",
    icon: MapTrifold,
    eyebrow: "AI FOR DISPATCH COMPANIES",
    title_lead: "Manage 5x more loads",
    title_accent: "per dispatcher.",
    title_close: "Without hiring a bigger team.",
    tagline: "AI reads rate cons, drafts driver texts, chases payments, and handles broker follow-ups — you just approve.",
    seo_title: "AI for Dispatch Companies · Scale Load Coverage Without Scaling Headcount",
    seo_description: "Dispatch companies use FreightPilot AI to book more loads per dispatcher by automating parsing, driver comms, and broker updates. 20-40 hours saved per dispatcher per week.",
    pain_points: [
      "Each new dispatcher costs $50-75k/year plus training — margins get squeezed",
      "You lose loads because rate cons sit unopened for hours",
      "Drivers texting the same 'where's my load?' question at the same time",
      "Broker portals need constant checking — you can't keep up",
      "New carriers want white-glove onboarding but you're maxed out",
    ],
    solutions: [
      { icon: "email", title: "6-second load parsing", detail: "Every rate con — DAT, Truckstop, direct broker email — becomes a structured load record instantly." },
      { icon: "dispatch", title: "AI-drafted driver texts", detail: "'Load PU 6am at Kroger — 40k lb produce. Deliver Fri by 8am. Rate $3,200. Confirm?' — sent from your number." },
      { icon: "customer", title: "Broker follow-ups on autopilot", detail: "Payment aging, load status, dispatch confirmations — AI drafts, you approve, sends from your Gmail." },
      { icon: "invoice", title: "Same-day settlement paperwork", detail: "Rate con + POD + invoice bundled and emailed to the factoring company or broker the moment the load closes." },
      { icon: "insights", title: "Per-dispatcher revenue tracking", detail: "See who's booking the most gross, catching the best rates, and generating the highest margins." },
    ],
    testimonial_placeholder: "You could be our first dispatch case study — pilot slots are open through Q1 2026.",
    proof_stat: "5x",
    proof_unit: "loads per dispatcher",
    proof_context: "Same team, more loads booked — because AI handles the repetitive work.",
    cta_primary: "Try live demo — no signup",
    cta_secondary: "Talk to founder",
  },

  brokers: {
    slug: "brokers",
    icon: Handshake,
    eyebrow: "AI FOR FREIGHT BROKERS",
    title_lead: "Cover more loads",
    title_accent: "with fewer phone calls.",
    title_close: "AI does the outreach.",
    tagline: "Post loads, chase carriers, and confirm bookings — with AI drafting the emails and texts so you just approve.",
    seo_title: "AI for Freight Brokers · Automate Carrier Outreach, Rate Negotiation & Load Coverage",
    seo_description: "Freight brokers use FreightPilot AI to cover more loads per broker by auto-drafting carrier outreach, drafting counter-offers with AI, and streamlining booking confirmations.",
    pain_points: [
      "Cold-calling 40 carriers to cover one difficult lane",
      "Rate negotiations dragging out for hours over email",
      "Carriers ghosting after you send the rate con",
      "Lost lanes because you didn't follow up in time",
      "Junior brokers burn out doing repetitive outreach",
    ],
    solutions: [
      { icon: "email", title: "AI Rate Negotiator", detail: "Paste a broker offer + your target rate. AI drafts a professional counter-offer email that lifts your average rate by $80-150/load." },
      { icon: "dispatch", title: "Auto-carrier outreach", detail: "Load posted → AI drafts personalized emails to your top 30 carriers in that lane. You approve, they send." },
      { icon: "customer", title: "Follow-up sequences", detail: "24hr / 72hr / 5-day automated nudges to carriers who opened but didn't reply. Nothing falls through the cracks." },
      { icon: "invoice", title: "Instant rate con delivery", detail: "Once a carrier confirms, rate con generated and emailed in one click. Signed PDF back in your dashboard." },
      { icon: "insights", title: "Rate intelligence", detail: "See historic rates for every lane you touch — bid smarter next time." },
    ],
    testimonial_placeholder: "You could be our first broker case study — pilot slots open Q1 2026.",
    proof_stat: "$80-150",
    proof_unit: "more per load",
    proof_context: "Average uplift when brokers use our AI Rate Negotiator on carrier offers.",
    cta_primary: "Try the AI Negotiator",
    cta_secondary: "Book a 15-min demo",
  },

  "owner-ops": {
    slug: "owner-ops",
    icon: User,
    eyebrow: "AI FOR OWNER OPERATORS",
    title_lead: "Get paid faster.",
    title_accent: "Chase fewer brokers.",
    title_close: "Drive more, type less.",
    tagline: "One-person operation? AI is your dispatcher, negotiator, and back-office. All for less than one hour of driving revenue per week.",
    seo_title: "AI for Owner Operators · Solo Truckers Automate Dispatch, Invoicing & Broker Follow-ups",
    seo_description: "Owner operators use FreightPilot AI as a personal AI dispatcher: reads rate cons, drafts counter-offers to broker offers, chases invoices, sends professional customer updates.",
    pain_points: [
      "Driving all day, then doing paperwork until midnight",
      "Getting lowballed by brokers because you don't have time to negotiate hard",
      "Chasing broker payments 60+ days out — cash flow is death",
      "Small companies undercutting you because they don't have your fixed costs",
      "You want to add a truck but you're already stretched thin",
    ],
    solutions: [
      { icon: "email", title: "Paste rate con → dispatched", detail: "In the truck stop, paste the email in on your phone — origin, destination, rate parsed in 6 seconds. Ready to run." },
      { icon: "dispatch", title: "AI counters broker offers", detail: "Broker offers $2 per mile? AI drafts a professional counter at your target rate. You send. Rates go up." },
      { icon: "customer", title: "Auto-updates keep brokers happy", detail: "Picked up, in transit, delivered — auto-emails from YOUR gmail. Brokers see you as 'the pro' and give you their next load." },
      { icon: "invoice", title: "Same-day invoicing", detail: "Delivered → invoice emailed same day. Factor accepts it faster. Money in bank quicker." },
      { icon: "insights", title: "My Pay Portal", detail: "Every paystub. Every load. YTD earnings. All in one place, from your phone." },
    ],
    testimonial_placeholder: "You could be our first owner-op case study — pilot slots open Q1 2026.",
    proof_stat: "$50",
    proof_unit: "per month",
    proof_context: "Less than one hour of driving revenue. Pays for itself the first time AI wins you a rate negotiation.",
    cta_primary: "Try live demo — no signup",
    cta_secondary: "Start free trial",
  },

  "fleet-managers": {
    slug: "fleet-managers",
    icon: ChartLineUp,
    eyebrow: "AI FOR FLEET MANAGERS",
    title_lead: "See everything.",
    title_accent: "Fix problems fast.",
    title_close: "Coach drivers smarter.",
    tagline: "Real-time operational visibility with AI that flags at-risk loads, coaches driver comms, and surfaces performance patterns your ELD can't see.",
    seo_title: "AI for Fleet Managers · Real-Time Operational Visibility & Driver Coaching",
    seo_description: "Fleet managers use FreightPilot AI for real-time load visibility, at-risk load detection, driver coaching prompts, and performance analytics beyond ELD data.",
    pain_points: [
      "Loads slipping late because you didn't know until the customer called",
      "Drivers going silent — you don't know why until it's too late",
      "Maintenance schedules ignored because paperwork is manual",
      "Broker complaints coming through the general inbox, missed for days",
      "You need CFO-grade financials but you're pulling them from 4 systems",
    ],
    solutions: [
      { icon: "email", title: "AI-drafted driver check-ins", detail: "Every 6 hours the AI drafts a personalized 'you good?' text based on load status. Drivers reply faster." },
      { icon: "dispatch", title: "Early warning on at-risk loads", detail: "Load 3 hours behind ETA + weather delay + driver going quiet? AI flags it before the customer calls." },
      { icon: "customer", title: "Complaint routing", detail: "Broker complaint email arrives → AI classifies, prioritizes, routes to right dispatcher. Nothing rots in the inbox." },
      { icon: "invoice", title: "Same-day P&L", detail: "Weekly payroll + fuel + revenue reconciled by the end of Sunday. See exact profit per truck." },
      { icon: "insights", title: "Performance analytics", detail: "Rev-per-mile per driver, on-time %, load rejection rate, empty miles. See who's carrying the fleet." },
    ],
    testimonial_placeholder: "You could be our first fleet case study — pilot slots open Q1 2026.",
    proof_stat: "24h",
    proof_unit: "earlier warning",
    proof_context: "AI flags at-risk loads a full day before customers ever notice — you save the account.",
    cta_primary: "Try live demo — no signup",
    cta_secondary: "Book a walkthrough",
  },
};

export const INDUSTRY_SLUGS = Object.keys(INDUSTRY_PAGES);
